export interface MatchDirectorState {
  elapsed: number;
  health: number;
  shield: number;
  mats: number;
  aliveBots: number;
  nearestBotDist: number;
  stormPhase: number;
  inLobby: boolean;
  isFinalCircle: boolean;
  pois?: MatchDirectorPoi[];
}

export type MatchDirectorPoi = {
  name: string;
  x: number;
  z: number;
  tier: 'hot' | 'warm' | 'cold';
};

export type MatchDirectorSuggestion =
  | 'none'
  | 'ease_pressure'
  | 'increase_pressure'
  | 'encourage_loot'
  | 'rotate_storm'
  | 'spawn_reinforcement_wave'
  | 'activate_hot_zone';

export type HotZoneState = {
  name: string;
  x: number;
  z: number;
  radius: number;
  lootMultiplier: number;
};

const SUGGESTION_COOLDOWN_S: Record<Exclude<MatchDirectorSuggestion, 'none'>, number> = {
  ease_pressure: 18,
  increase_pressure: 34,
  encourage_loot: 38,
  rotate_storm: 28,
  spawn_reinforcement_wave: 9999,
  activate_hot_zone: 9999,
};

const HOT_ZONE_RADIUS = 38;
const HOT_ZONE_LOOT_MULTIPLIER = 1.55;
const WAVE_MIN_ELAPSED = 78;
const WAVE_MAX_ELAPSED = 210;
const HOT_ZONE_MIN_ELAPSED = 68;
export const FINAL_CIRCLE_BOT_CAP = 3;

/** Lightweight pacing director: reads match state and emits rare, conservative nudges. */
export class FpsMatchDirector {
  private elapsed = 0;
  private quietTimer = 0;
  private previousStormPhase = 1;
  private waveTriggered = false;
  private hotZoneTriggered = false;
  private hotZone: HotZoneState | null = null;
  private readonly cooldowns: Record<Exclude<MatchDirectorSuggestion, 'none'>, number> = {
    ease_pressure: 0,
    increase_pressure: 0,
    encourage_loot: 0,
    rotate_storm: 0,
    spawn_reinforcement_wave: 0,
    activate_hot_zone: 0,
  };

  resetForMatch(): void {
    this.elapsed = 0;
    this.quietTimer = 0;
    this.previousStormPhase = 1;
    this.waveTriggered = false;
    this.hotZoneTriggered = false;
    this.hotZone = null;
    for (const key of Object.keys(this.cooldowns) as Array<Exclude<MatchDirectorSuggestion, 'none'>>) {
      this.cooldowns[key] = 0;
    }
  }

  getActiveHotZone(): HotZoneState | null {
    return this.hotZone;
  }

  shouldCapFinalCircleBots(stormPhase: number, isFinalCircle: boolean): boolean {
    return stormPhase >= 5 || isFinalCircle;
  }

  update(dt: number, state: MatchDirectorState): MatchDirectorSuggestion {
    this.elapsed = Math.max(this.elapsed + Math.max(0, dt), state.elapsed);
    this.tickCooldowns(dt);

    if (state.inLobby || state.aliveBots <= 0) {
      this.quietTimer = 0;
      this.previousStormPhase = Math.max(1, state.stormPhase);
      return 'none';
    }

    const hotZoneSuggestion = this.tryHotZone(state);
    if (hotZoneSuggestion !== 'none') return hotZoneSuggestion;

    const waveSuggestion = this.tryReinforcementWave(state);
    if (waveSuggestion !== 'none') return waveSuggestion;

    const totalVitality = state.health + state.shield;
    const botNearby = Number.isFinite(state.nearestBotDist) && state.nearestBotDist <= 8.5;
    const underThreat = botNearby || state.health <= 32 || totalVitality <= 45;
    if (underThreat) {
      this.quietTimer = 0;
      return this.trySuggest('ease_pressure');
    }

    const earlyLowMats = state.elapsed <= 100 && state.mats < 55;
    const lowLootSignals = totalVitality < 120 || state.shield < 35;
    if (earlyLowMats && lowLootSignals) {
      return this.trySuggest('encourage_loot');
    }

    const stormAdvanced = state.stormPhase > this.previousStormPhase;
    this.previousStormPhase = Math.max(this.previousStormPhase, state.stormPhase);
    if (stormAdvanced || (state.stormPhase >= 4 && state.elapsed > 90)) {
      return this.trySuggest('rotate_storm');
    }

    const noNearbyFight = !Number.isFinite(state.nearestBotDist) || state.nearestBotDist >= 30;
    if (noNearbyFight && state.elapsed >= 45 && totalVitality >= 80) {
      this.quietTimer += dt;
    } else {
      this.quietTimer = 0;
    }

    if (this.quietTimer >= 24) {
      this.quietTimer = 0;
      return this.trySuggest('increase_pressure');
    }

    return 'none';
  }

  private tryReinforcementWave(state: MatchDirectorState): MatchDirectorSuggestion {
    if (this.waveTriggered) return 'none';
    if (state.elapsed < WAVE_MIN_ELAPSED || state.elapsed > WAVE_MAX_ELAPSED) return 'none';
    if (state.aliveBots >= 8 || state.aliveBots <= 1) return 'none';

    const lull = this.quietTimer >= 18 || state.aliveBots <= 5;
    if (!lull) return 'none';

    this.waveTriggered = true;
    return this.trySuggest('spawn_reinforcement_wave');
  }

  private tryHotZone(state: MatchDirectorState): MatchDirectorSuggestion {
    if (this.hotZoneTriggered) return 'none';
    if (state.elapsed < HOT_ZONE_MIN_ELAPSED) return 'none';
    if (!state.pois || state.pois.length === 0) return 'none';

    const stormReady = state.stormPhase >= 3 || state.elapsed >= 95;
    if (!stormReady) return 'none';

    const candidates = state.pois.filter((poi) => poi.tier === 'hot' || poi.tier === 'warm');
    const pool = candidates.length > 0 ? candidates : state.pois;
    const pick = pool[Math.floor(Math.random() * pool.length)]!;
    this.hotZone = {
      name: pick.name,
      x: pick.x,
      z: pick.z,
      radius: HOT_ZONE_RADIUS,
      lootMultiplier: HOT_ZONE_LOOT_MULTIPLIER,
    };
    this.hotZoneTriggered = true;
    return this.trySuggest('activate_hot_zone');
  }

  private tickCooldowns(dt: number): void {
    for (const key of Object.keys(this.cooldowns) as Array<Exclude<MatchDirectorSuggestion, 'none'>>) {
      this.cooldowns[key] = Math.max(0, this.cooldowns[key] - dt);
    }
  }

  private trySuggest(suggestion: Exclude<MatchDirectorSuggestion, 'none'>): MatchDirectorSuggestion {
    if (this.cooldowns[suggestion] > 0) return 'none';
    this.cooldowns[suggestion] = SUGGESTION_COOLDOWN_S[suggestion];
    return suggestion;
  }
}
